import { useQuery } from "@apollo/client";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { AIScheduler } from "@clockwise/design-system/icons";
import { Tooltip } from "@material-ui/core";
import classNames from "classnames";
import { useRouter } from "found";
import { compact, noop } from "lodash";
import {
  ArrowSquareIn,
  Atom,
  BugBeetle,
  ChartBar,
  CreditCard,
  Graph,
  House,
  IdentificationCard,
  LightbulbFilament,
  LinkSimple,
  PaperPlaneRight,
  Question,
  ShieldCheckered,
  Sparkle,
  UserList,
} from "phosphor-react";
import { default as React, createContext, useContext, useEffect, useRef } from "react";
import { ClockwiseLogo } from "../../components/svgs/ClockwiseLogo";
import { ClockwiseLogoText } from "../../components/svgs/ClockwiseLogoText";
import { ExpandSidebar } from "../../components/svgs/ExpandSidebar";
import { OrganizationSettings } from "../../components/svgs/OrganizationSettings";
import { TeamSettings } from "../../components/svgs/TeamSettings";
import { appPaths } from "../../constants/route.constants";
import useHasScrollbar from "../../hooks/useHasScrollbar";
import { getSortedCollatedTeamInfo } from "../../util/web-settings";
import { Badge } from "../badge/Badge";
import { TeamAvatar } from "../team-avatar/TeamAvatar";
import CreateOrJoinTeamButton from "./CreateOrJoinTeamButton";
import { HomeNavTeamsDocument } from "./__generated__/HomeNavTeams.generated";

export type NavContextType = {
  collapsed: boolean;
  isFreePlan: boolean;
  scrollbared: boolean;
  setScrollbared: (...args: any[]) => void;
  toggleCollapsed: (...args: any[]) => void;
};

/**
 * @deprecated
 */
export const NavContext = createContext<NavContextType>({
  collapsed: true,
  isFreePlan: false,
  scrollbared: false,
  setScrollbared: noop,
  toggleCollapsed: noop,
});

/**
 * @deprecated
 */
export function NavItem({
  Icon,
  RightIcon = null,
  label,
  path,
  badge = null,
  isExternalPath = false,
}: {
  Icon: React.ReactElement;
  label: string;
  path?: string;
  isExternalPath?: boolean;
  badge?: React.ReactElement | null;
  RightIcon?: React.ReactElement | null;
}) {
  const { collapsed, scrollbared } = useContext(NavContext);
  const { match, router } = useRouter();
  const isActive = path
    ? router?.isActive?.(match, router?.createLocation?.(path), { exact: true })
    : false;

  const target = isExternalPath ? "_blank" : undefined;

  return (
    <Tooltip className="cw-body-lg" title={collapsed ? label : ""} placement={"right"}>
      <li
        className={classNames(
          "cw-flex cw-flex-row cw-gap-4 cw-cursor-pointer cw-body-base cw-h-10 hover:cw-bg-brand-inset-hover hover:cw-bg-opacity-20",
          {
            "cw-bg-accent-emphasis-pressed": isActive,
            "group-hover:cw-rounded-r": scrollbared,
          },
        )}
      >
        <a
          className={classNames(
            "cw-cursor-pointer cw-body-base cw-inline cw-w-full cw-h-10 cw-no-underline",
          )}
          href={path}
          target={target}
          aria-label={`${label}`}
          onClick={(event) => {
            if (isExternalPath) {
              // the href will handle this
              return;
            }

            // We need `href="{path}"` because this makes the browser interpret the <a /> as a hyperlink.
            // Without it, we lose full keyboard support (tab and enter). However a side effect,
            // is if you use the href, it'll reload the app since it's like typing a new URL into the browser's address bar.
            // And we want `href="{path}"` in order for the browser to show the correct link preview (in the bottom of browser)
            // Thus we add this `preventDefault` stops the browser from reloading app!
            event.preventDefault();

            path && router.push(path);
          }}
        >
          <div className="cw-flex cw-flex-row cw-flex-grow cw-gap-4 cw-h-10">
            <div className="cw-ml-6 cw-self-center cw-h-6 cw-w-6">{Icon}</div>
            <div
              className={classNames("cw-self-center cw-whitespace-nowrap cw-flex cw-flex-row", {
                "cw-text-onEmphasis cw-m-1 cw-ml--1": !collapsed,
                "cw-invisible": collapsed,
              })}
            >
              {label}
              {!!badge && <div className="cw-self-center cw-ml-1.5">{badge}</div>}
              {!!RightIcon && <div className="cw-self-center cw-h-4 cw-w-4">{RightIcon}</div>}
            </div>
          </div>
        </a>
      </li>
    </Tooltip>
  );
}

export const YOUR_IDEAL_DAY_NAV_ITEM_LABEL = "Your ideal day";
export const PREFERENCES_NAV_ITEM_LABEL = "Preferences";

const MainSection = ({
  shouldShowGetStartedLandingPage,
}: {
  shouldShowGetStartedLandingPage: boolean;
}) => {
  return (
    <section className="cw-flex cw-flex-col cw-justify-between cw-h-full">
      <ul
        className={classNames("cw-flex cw-flex-col cw-w-full cw-justify-start cw-p-0 cw-m-0", {
          "cw-mt-1": true /* Chrome Browser keyboard tab-highlighting requires 4px of padding or margin to work! */,
        })}
        aria-label="Main Navigation Section"
      >
        <NavItem
          Icon={<AIScheduler fill="white" className="cw-w-6 cw-h-6" />}
          label="AI Scheduler"
          path={appPaths.ai}
          badge={<Badge label="Beta" />}
        />
        <NavItem
          Icon={<House weight="regular" color="white" />}
          path={appPaths.landing}
          label={shouldShowGetStartedLandingPage ? "Get Started" : "Home"}
        />
        <NavItem
          Icon={<LightbulbFilament weight="regular" color="white" />}
          label={YOUR_IDEAL_DAY_NAV_ITEM_LABEL}
          path={appPaths.accountPreferences}
        />
        <NavItem
          Icon={<Sparkle weight="regular" color="white" />}
          label="Flexible meetings"
          path={appPaths.flexibleMeetings}
        />
        <NavItem
          Icon={<ShieldCheckered weight="regular" color="white" />}
          label="Flexible holds"
          path={appPaths.flexibleHolds}
        />
        <NavItem
          Icon={<LinkSimple weight="regular" color="white" />}
          label="Scheduling links"
          path={appPaths.schedulingLinks}
        />
        <NavItem
          Icon={<Atom weight="regular" color="white" />}
          label="Connected apps"
          path={appPaths.appSettings}
        />
      </ul>
    </section>
  );
};

function getCurrentOrg(viewer: any) {
  if (!viewer?.user?.orgs?.edges?.length) {
    return null;
  }

  // find the right org depending on orgId state
  const edges: any[] = compact(viewer.user.orgs.edges ?? []);
  const org = edges[0]?.node;

  // if we haven't found anything, return null
  if (!org) {
    return null;
  }

  return org;
}

const sectionHeaderClasses = "cw-flex cw-font-bold cw-ml-6 cw-body-base cw-text-onEmphasis cw-h-10";
const TeamsSection = () => {
  const { collapsed } = useContext(NavContext);

  const { data, loading } = useQuery(HomeNavTeamsDocument);

  const viewer = getValue(data?.viewer, "Viewer");
  const org = getCurrentOrg(viewer);

  if (loading || !org) {
    return null;
  }

  const teamsMenuList = getSortedCollatedTeamInfo(org);

  return (
    <section className="cw-flex cw-flex-col cw-justify-between cw-h-full">
      <div className="cw-flex cw-flex-col cw-w-full cw-justify-start cw-p-0 cw-m-0">
        <div className={classNames(sectionHeaderClasses, { "cw-hidden": collapsed })}>
          {" "}
          <header className="cw-body-base cw-font-bold cw-self-center cw-text-onEmphasis">
            Teams
          </header>
        </div>
        <ul className="cw-p-0 cw-m-0" aria-label="Teams Navigation Section">
          {teamsMenuList.map(({ teamId, teamName }) => {
            return (
              <NavItem
                key={teamId}
                Icon={<TeamAvatar id={teamId} teamName={teamName} />}
                path={`${appPaths.teamSettings}/${teamId}`}
                label={teamName}
              />
            );
          })}

          <NavItem Icon={<TeamSettings />} path={appPaths.teamsSettings} label="Team settings" />
          <CreateOrJoinTeamButton />
        </ul>
      </div>
    </section>
  );
};

const OrganizationSection = () => {
  const { collapsed } = useContext(NavContext);

  return (
    <section className="cw-flex cw-flex-col cw-justify-between cw-h-full">
      <div className="cw-flex cw-flex-col cw-w-full cw-justify-start cw-p-0 cw-m-0">
        <div className={classNames(sectionHeaderClasses, { "cw-hidden": collapsed })}>
          <header className="cw-body-base cw-font-bold cw-self-center cw-text-onEmphasis">
            Organization
          </header>
        </div>
        <ul className="cw-p-0 cw-m-0" aria-label="Organization Navigation Section">
          <NavItem
            Icon={<ChartBar weight="regular" color="white" />}
            path={appPaths.analytics}
            label="Analytics"
          />
          <NavItem
            Icon={<UserList weight="regular" color="white" />}
            path={appPaths.members}
            label="Users"
          />
          <NavItem
            Icon={<CreditCard weight="regular" color="white" />}
            path={appPaths.plansAndBilling}
            label="Plans & billing"
          />
          <NavItem
            Icon={<OrganizationSettings />}
            path={appPaths.orgSettings}
            label="Organization settings"
          />
        </ul>
      </div>
    </section>
  );
};

const FooterSection = ({ showExperiments = false }: { showExperiments?: boolean }) => {
  const { collapsed } = useContext(NavContext);

  return (
    <section className="cw-flex cw-flex-col cw-justify-between">
      <ul
        className={classNames(
          "cw-flex cw-flex-col cw-w-full cw-justify-start cw-p-0 cw-m-0 cw-mb-4",
        )}
        aria-label="Footer Navigation Section"
      >
        <NavItem
          Icon={<PaperPlaneRight weight="regular" color="white" />}
          label={"Share Clockwise"}
          path={`${appPaths.landing}?invite=true`}
        />
        <NavItem
          Icon={<IdentificationCard weight="regular" color="white" />}
          path={appPaths.preferences}
          label={PREFERENCES_NAV_ITEM_LABEL}
        />
        <NavItem
          Icon={<Question weight="regular" color="white" />}
          path="https://getclockwise.helpscoutdocs.com/"
          isExternalPath
          label="Get support"
          RightIcon={
            !collapsed ? (
              <ArrowSquareIn
                className="cw-ml-1.5"
                height={20}
                width={20}
                color="#B6FF80"
                fill="none"
              />
            ) : null
          }
        />
        {showExperiments && (
          <NavItem
            Icon={<BugBeetle weight="regular" color="white" />}
            path={appPaths.experiments}
            label="Experiments"
          />
        )}
        {showExperiments && (
          <NavItem
            Icon={<Graph weight="regular" color="white" />}
            path={appPaths.graphiql}
            label="GraphiQL"
          />
        )}
      </ul>
    </section>
  );
};

const Divider = () => (
  <div className="cw-border-solid cw-border-0 cw-border-t cw-border-white cw-border-opacity-20 cw-my-4" />
);

const Header = () => {
  const { router } = useRouter();
  const { isFreePlan, toggleCollapsed, collapsed } = useContext(NavContext);
  const sidebarOpen = !collapsed;

  return (
    <div className=" cw-select-none">
      <div className="cw-flex cw-flex-row cw-ml-6 cw-h-[72px]">
        {sidebarOpen ? (
          <div className="cw-flex cw-flex-grow">
            <a
              className="cw-flex cw-items-center cw-flex-grow cw-flex-row cw-ml-[0.5px] cw-no-underline"
              href="#"
              aria-label="Clockwise Logo - Link to Clockwise Home"
              onClick={(event) => {
                // We need `href="#"` because this makes the browser interpret the <a /> as a hyperlink.
                // Without it, we lose full keyboard support (tab and enter). However a side effect,
                // is it added `#` to the URL needlessly.
                // Thus we add this `preventDefault` stops the browser from adding `#` to the end of the URL needlessly
                event.preventDefault();

                router.push(appPaths.landing);
              }}
            >
              <div className="cw-h-6 cw-transition-all">
                <ClockwiseLogo />
              </div>
              <div className="cw-ml-[1.2rem]">
                <ClockwiseLogoText />
              </div>
              {isFreePlan && (
                <div className="cw-text-accent cw-text-[10px] cw-font-bold cw-font-[Helvetica] cw-ml-1.5 cw-uppercase cw-bg-default cw-rounded cw-py-1 cw-px-1 cw-text-center">
                  Free
                </div>
              )}
            </a>
            <Tooltip className="cw-body-lg" title="Hide Navigation" placement="right">
              <div
                className="cw-self-center cw-mr-4 cw-h-6 cw-w-6 cw-flex-grow cw-flex cw-justify-end cw-group"
                tabIndex={0}
                // The inner <button> will not show a hitbox for keyboard user since opacity is 0 by default
                // 0-opacity elements do not get a broswer hitbox when tabbing
                // Thus we put tabIndex on this wrapping <div> to show hitboxes for keyboard users
                // This also causes the side effect of making the div and the button `tabbable`
                // but many websites do that
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    toggleCollapsed();
                  }
                }}
              >
                <button
                  className="cw-border-none cw-bg-transparent cw-cursor-pointer cw-m-0 cw-p-0 cw-transition-all cw-opacity-0 group-hover:cw-opacity-100 group-focus-visible:cw-opacity-100"
                  aria-label="Collapse navigation"
                  onClick={toggleCollapsed}
                  tabIndex={-1}
                >
                  <ExpandSidebar />
                </button>
              </div>
            </Tooltip>
          </div>
        ) : (
          <Tooltip className="cw-body-lg" title="Show Navigation" placement={"right"}>
            <div className="cw-relative cw-flex cw-items-center">
              <div
                className="group-hover:cw-opacity-0 cw-transition-all cw-h-6 cw-w-6 focus-visible:cw-z-0 cw-peer"
                tabIndex={0}
                // The inner <button> will not show a hitbox for keyboard user since opacity is 0 by default
                // 0-opacity elements do not get a broswer hitbox when tabbing
                // Thus we put tabIndex on this wrapping <div> to show hitboxes for keyboard users
                // This also causes the side effect of making the div and the button `tabbable`
                // but many websites do that
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    toggleCollapsed();
                  }
                }}
              >
                <ClockwiseLogo />
              </div>
              <div className="cw-mr-1 cw-cursor-pointer cw-absolute cw-bg-nav cw-rotate-180 cw-h-6 cw-w-6 cw-transition-all cw-opacity-0 group-hover:cw-opacity-100 peer-focus-visible:cw-opacity-100 peer-focus-visible:cw-z-10">
                <button
                  className="cw-border-none  cw-bg-transparent cw-cursor-pointer cw-m-0 cw-p-0"
                  aria-label="Expand navigation"
                  onClick={toggleCollapsed}
                  tabIndex={-1}
                >
                  <ExpandSidebar />
                </button>
              </div>
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

/**
 * @deprecated
 */
export const SidebarTabs = ({
  showExperiments = false,
  shouldShowGetStartedLandingPage = false,
}: {
  showExperiments?: boolean;
  shouldShowGetStartedLandingPage?: boolean;
}) => {
  const { collapsed, scrollbared, setScrollbared } = useContext(NavContext);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasScrollbar] = useHasScrollbar(scrollRef);

  useEffect(() => {
    setScrollbared(!collapsed && !!hasScrollbar);
  }, [collapsed, hasScrollbar, setScrollbared]);

  return (
    <nav className="cw-group cw-flex cw-flex-col cw-justify-between cw-h-full cw-transition-all">
      <div className="cw-h-[72px]">
        <Header />
      </div>
      <div className={classNames({ "group-hover:cw-pr-1": scrollbared })}>
        <div
          ref={scrollRef}
          className={classNames(
            "cw-h-[calc(100vh-72px)] cw-grow cw-flex cw-flex-col cw-justify-between cw-overflow-x-hidden cw-invisible",
            {
              "cw-overflow-y-auto cw-scrollbar-thin cw-scrollbar-track-[#ffffff00] cw-scrollbar-thumb-[#ffffff1a] cw-scrollbar-rounded-sm group-hover:cw-visible": scrollbared,
              "cw-overflow-y-auto cw-scrollbar-thin cw-scrollbar-track-[#ffffff00] cw-scrollbar-thumb-[#ffffff00] group-hover:cw-visible": !scrollbared,
            },
          )}
        >
          <div className="cw-visible cw-h-full cw-flex cw-flex-col">
            <div
              className={classNames("cw-grow", {
                "group-hover:cw-pr-3": scrollbared,
              })}
            >
              <div>
                <MainSection shouldShowGetStartedLandingPage={shouldShowGetStartedLandingPage} />
              </div>
              <div className="cw-flex cw-flex-col">
                <div>
                  <Divider />
                  <TeamsSection />
                </div>
                <div>
                  <Divider />
                  <OrganizationSection />
                </div>
              </div>
            </div>
            <div className={classNames({ "group-hover:cw-pr-3": scrollbared })}>
              <Divider />
              <FooterSection showExperiments={showExperiments} />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
